import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import useFocusTrap from '../hooks/useFocusTrap';
import Input from './common/ui/Input';
import { SORT_OPTIONS, COMPLETION_OPTIONS, ORIGIN_OPTIONS } from '../constants/filterOptions';
import './LibraryFiltersPanel.css';

const pairedNumberFields = [
  {
    id: 'rating',
    minField: 'minRating',
    maxField: 'maxRating',
    label: 'Average rating',
    minPlaceholder: '0.0',
    maxPlaceholder: '10.0',
    step: '0.1',
  },
  {
    id: 'ratingCount',
    minField: 'minRatingCount',
    maxField: 'maxRatingCount',
    label: 'Rating count',
    minPlaceholder: 'Min',
    maxPlaceholder: 'Max',
  },
  {
    id: 'chapterCount',
    minField: 'minChapterCount',
    maxField: 'maxChapterCount',
    label: 'Chapter count',
    minPlaceholder: 'Min',
    maxPlaceholder: 'Max',
  },
  {
    id: 'viewCount',
    minField: 'minViewCount',
    maxField: 'maxViewCount',
    label: 'Views',
    minPlaceholder: 'Min',
    maxPlaceholder: 'Max',
  },
];

const CollapsibleSection = ({ title, children, defaultOpen = false, summary }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className={`library-filters__section ${isOpen ? 'library-filters__section--open' : ''}`}>
      <button
        type="button"
        className="library-filters__section-header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="library-filters__section-title-wrapper">
          <h3 className="library-filters__section-title">{title}</h3>
          {summary && !isOpen && <span className="library-filters__section-summary">{summary}</span>}
        </div>
        <span className="library-filters__section-toggle-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div className="library-filters__section-body">
        <div className="library-filters__section-content">
          {children}
        </div>
      </div>
    </section>
  );
};

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  summary: PropTypes.string,
};

const LibraryFiltersPanel = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  filters,
  onChange,
  genres,
  tags,
  contentWarnings = [],
}) => {
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useFocusTrap(panelRef, isOpen);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const [genreSelection, setGenreSelection] = useState(() => {
    const include = new Set((filters.includeGenreIds ?? []).map(String));
    const exclude = new Set((filters.excludeGenreIds ?? []).map(String));
    return { include, exclude };
  });

  const [tagSelection, setTagSelection] = useState(() => {
    const include = new Set((filters.includeTagIds ?? []).map(String));
    const exclude = new Set((filters.excludeTagIds ?? []).map(String));
    return { include, exclude };
  });

  // Content warnings are exclude-only — including a warning as a discovery
  // signal doesn't make sense, so there's no include/exclude cycle here.
  const [warningSelection, setWarningSelection] = useState(() => (
    new Set((filters.excludeContentWarningIds ?? []).map(String))
  ));

  const [genreSearch, setGenreSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');

  const genreLookup = useMemo(() => {
    const map = new Map();
    genres.forEach((genre) => {
      if (genre?.id != null) {
        map.set(String(genre.id), genre);
      }
    });
    return map;
  }, [genres]);

  const tagLookup = useMemo(() => {
    const map = new Map();
    tags.forEach((tag) => {
      if (tag?.id != null) {
        map.set(String(tag.id), tag);
      }
    });
    return map;
  }, [tags]);

  const warningLookup = useMemo(() => {
    const map = new Map();
    contentWarnings.forEach((warning) => {
      if (warning?.id != null) {
        map.set(String(warning.id), warning);
      }
    });
    return map;
  }, [contentWarnings]);

  const filteredGenres = useMemo(() => {
    if (!genreSearch.trim()) return genres;
    const lower = genreSearch.toLowerCase();
    return genres.filter((g) => g.name.toLowerCase().includes(lower));
  }, [genres, genreSearch]);

  const filteredTags = useMemo(() => {
    if (!tagSearch.trim()) return tags;
    const lower = tagSearch.toLowerCase();
    return tags.filter((t) => t.name.toLowerCase().includes(lower));
  }, [tags, tagSearch]);

  useEffect(() => {
    setGenreSelection({
      include: new Set((filters.includeGenreIds ?? []).map(String)),
      exclude: new Set((filters.excludeGenreIds ?? []).map(String)),
    });
    setTagSelection({
      include: new Set((filters.includeTagIds ?? []).map(String)),
      exclude: new Set((filters.excludeTagIds ?? []).map(String)),
    });
    setWarningSelection(new Set((filters.excludeContentWarningIds ?? []).map(String)));
  }, [
    filters.includeGenreIds,
    filters.excludeGenreIds,
    filters.includeTagIds,
    filters.excludeTagIds,
    filters.excludeContentWarningIds,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement;
      panelRef.current?.focus?.();
    } else if (previouslyFocusedRef.current) {
      previouslyFocusedRef.current.focus?.();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const cycleTriState = (category, rawValue) => {
    const value = String(rawValue);
    if (category === 'genres') {
      setGenreSelection((current) => {
        const include = new Set(current.include);
        const exclude = new Set(current.exclude);

        if (!include.has(value) && !exclude.has(value)) {
          include.add(value);
        } else if (include.has(value)) {
          include.delete(value);
          exclude.add(value);
        } else {
          exclude.delete(value);
        }

        onChange('includeGenreIds', Array.from(include));
        onChange('excludeGenreIds', Array.from(exclude));

        return { include, exclude };
      });
    } else {
      setTagSelection((current) => {
        const include = new Set(current.include);
        const exclude = new Set(current.exclude);

        if (!include.has(value) && !exclude.has(value)) {
          include.add(value);
        } else if (include.has(value)) {
          include.delete(value);
          exclude.add(value);
        } else {
          exclude.delete(value);
        }

        onChange('includeTagIds', Array.from(include));
        onChange('excludeTagIds', Array.from(exclude));

        return { include, exclude };
      });
    }
  };

  const toggleWarningExclusion = (rawValue) => {
    const value = String(rawValue);
    setWarningSelection((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      onChange('excludeContentWarningIds', Array.from(next));
      return next;
    });
  };

  const handleWarningClick = (value) => (event) => {
    event.preventDefault();
    toggleWarningExclusion(value);
  };

  const handleWarningKeyDown = (value) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleWarningExclusion(value);
    }
  };

  const getTriState = (category, rawValue) => {
    const value = String(rawValue);
    const selection = category === 'genres' ? genreSelection : tagSelection;
    if (selection.include.has(value)) {
      return 'include';
    }
    if (selection.exclude.has(value)) {
      return 'exclude';
    }
    return 'none';
  };

  const handleTriCheckboxClick = (category, value) => (event) => {
    event.preventDefault();
    cycleTriState(category, value);
  };

  const handleTriCheckboxKeyDown = (category, value) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      cycleTriState(category, value);
    }
  };

  const handleNumberChange = (field) => (event) => {
    onChange(field, event.target.value);
  };

  const handleChipRemove = (field, value) => {
    const next = (filters[field] ?? []).filter((item) => String(item) !== String(value));
    onChange(field, next);
  };

  const renderSelectedChips = (field, lookup) => {
    const values = filters[field] ?? [];
    if (!values.length) {
      return null;
    }

    const isExclude = field.startsWith('exclude');

    return (
      <div className={`library-filters__active-group ${isExclude ? 'library-filters__active-group--exclude' : ''}`}>
        <span className="library-filters__active-label">{isExclude ? 'Excluded:' : 'Included:'}</span>
        <ul className="library-filters__chips" aria-live="polite">
          {values.map((rawValue) => {
            const key = String(rawValue);
            const item = lookup.get(key);
            const label = item?.name ?? `ID ${key}`;
            return (
              <li key={key} className={`library-filters__chip ${isExclude ? 'library-filters__chip--exclude' : 'library-filters__chip--include'}`}>
                <span>{label}</span>
                <button
                  type="button"
                  className="library-filters__chip-remove"
                  onClick={() => handleChipRemove(field, key)}
                  aria-label={`Remove ${label}`}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderTriStateList = (category, items, groupLabelId) => {
    return (
      <div
        className="library-filters__multiselect-list"
        role="group"
        aria-labelledby={groupLabelId}
      >
        {items.map((item) => {
          const value = String(item.id);
          const state = getTriState(category, value);
          
          let icon = null;
          if (state === 'include') {
            icon = (
              <svg className="library-filters__state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            );
          } else if (state === 'exclude') {
             icon = (
              <svg className="library-filters__state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            );
          }

          return (
            <div
              key={`${category}-${value}`}
              className={`library-filters__multiselect-option library-filters__multiselect-option--${state}`}
              onClick={handleTriCheckboxClick(category, value)}
              onKeyDown={handleTriCheckboxKeyDown(category, value)}
              tabIndex={0}
              role="checkbox"
              aria-checked={state === 'include' ? 'true' : state === 'exclude' ? 'mixed' : 'false'}
            >
              <div className="library-filters__checkbox-visual">
                {icon}
              </div>
              <span className="library-filters__option-label">{item.name}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const getActiveCount = (includeKey, excludeKey) => {
    const inc = filters[includeKey]?.length || 0;
    const exc = filters[excludeKey]?.length || 0;
    if (inc === 0 && exc === 0) return '';
    return `${inc} included, ${exc} excluded`;
  };

  const getWarningSummary = () => {
    const parts = [];
    if (filters.hideMature) {
      parts.push('18+ hidden');
    }
    const excluded = filters.excludeContentWarningIds?.length || 0;
    if (excluded > 0) {
      parts.push(`${excluded} excluded`);
    }
    return parts.join(', ');
  };

  const renderInclusionModeToggle = (field, label) => {
    const mode = filters[field] || 'AND';
    return (
      <div className="library-filters__mode-toggle">
        <span className="library-filters__mode-label">{label}</span>
        <div className="library-filters__toggle-group">
          <button
            type="button"
            className={`library-filters__toggle-btn ${mode === 'AND' ? 'active' : ''}`}
            onClick={() => onChange(field, 'AND')}
          >
            Match All
          </button>
          <button
            type="button"
            className={`library-filters__toggle-btn ${mode === 'OR' ? 'active' : ''}`}
            onClick={() => onChange(field, 'OR')}
          >
            Match Any
          </button>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="library-filters library-filters--open" role="presentation">
      <div className="library-filters__backdrop" onClick={onClose} aria-hidden="true" />
      <aside
        className="library-filters__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="library-filters-title"
        ref={panelRef}
        tabIndex={-1}
      >
        <header className="library-filters__header">
          <div>
            <h2 id="library-filters-title">Filter & Sort</h2>
            <p className="library-filters__subtitle">Refine your library view</p>
          </div>
          <button 
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={onClose} 
            aria-label="Close filters"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </header>

        <div className="library-filters__body">
          <div className="library-filters__sections">
            <CollapsibleSection title="Sort & Status" defaultOpen={true}>
              <div className="library-filters__field-group library-filters__field-group--split">
                <div className="library-filters__field">
                  <label htmlFor="library-filters-sort">Sort by</label>
                  <div className="select-wrapper">
                    <select
                      id="library-filters-sort"
                      value={filters.sort}
                      onChange={(event) => onChange('sort', event.target.value)}
                      className="ui-input"
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <svg className="select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
                <div className="library-filters__field">
                  <label htmlFor="library-filters-status">Status</label>
                  <div className="select-wrapper">
                    <select
                      id="library-filters-status"
                      value={filters.isCompleted ?? 'any'}
                      onChange={(event) => onChange('isCompleted', event.target.value)}
                      className="ui-input"
                    >
                      {COMPLETION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                     <svg className="select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Origin" defaultOpen={false} summary={filters.originCountry || ''}>
              <div className="library-filters__field">
                <label htmlFor="library-filters-origin">Country of origin</label>
                <div className="select-wrapper">
                  <select
                    id="library-filters-origin"
                    value={filters.originCountry ?? ''}
                    onChange={(event) => onChange('originCountry', event.target.value)}
                    className="ui-input"
                  >
                    {ORIGIN_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <svg className="select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Metrics" defaultOpen={false}>
              <div className="library-filters__metrics-grid">
                {pairedNumberFields.map((fieldConfig) => (
                  <div key={fieldConfig.id} className="library-filters__field-pair">
                    <span className="library-filters__field-pair-label">{fieldConfig.label}</span>
                    <div className="library-filters__field-pair-inputs">
                      <Input
                        id={`${fieldConfig.minField}-input`}
                        type="number"
                        inputMode="decimal"
                        placeholder={fieldConfig.minPlaceholder}
                        value={filters[fieldConfig.minField] ?? ''}
                        step={fieldConfig.step}
                        onChange={handleNumberChange(fieldConfig.minField)}
                        aria-label={`${fieldConfig.label} minimum`}
                        className="metrics-input"
                      />
                      <span className="library-filters__range-separator">to</span>
                      <Input
                        id={`${fieldConfig.maxField}-input`}
                        type="number"
                        inputMode="decimal"
                        placeholder={fieldConfig.maxPlaceholder}
                        value={filters[fieldConfig.maxField] ?? ''}
                        step={fieldConfig.step}
                        onChange={handleNumberChange(fieldConfig.maxField)}
                        aria-label={`${fieldConfig.label} maximum`}
                        className="metrics-input"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            <CollapsibleSection 
              title="Genres" 
              defaultOpen={false} 
              summary={getActiveCount('includeGenreIds', 'excludeGenreIds')}
            >
              <div className="library-filters__active-summaries">
                {renderSelectedChips('includeGenreIds', genreLookup)}
                {renderSelectedChips('excludeGenreIds', genreLookup)}
              </div>
              {renderInclusionModeToggle('genreInclusionMode', 'Logic')}
              <div className="library-filters__search-container">
                <Input
                  type="text"
                  placeholder="Search genres..."
                  value={genreSearch}
                  onChange={(e) => setGenreSearch(e.target.value)}
                  aria-label="Search genres"
                  leftIcon={
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  }
                  fullWidth
                />
              </div>
              <div className="library-filters__field library-filters__field--multiselect">
                <span className="library-filters__field-helper" id="library-filters-genres-label">
                  Tap to cycle: <strong className="text-include">Include</strong> → <strong className="text-exclude">Exclude</strong> → Clear
                </span>
                {renderTriStateList('genres', filteredGenres, 'library-filters-genres-label')}
              </div>
            </CollapsibleSection>

            <CollapsibleSection 
              title="Tags" 
              defaultOpen={false}
              summary={getActiveCount('includeTagIds', 'excludeTagIds')}
            >
              <div className="library-filters__active-summaries">
                {renderSelectedChips('includeTagIds', tagLookup)}
                {renderSelectedChips('excludeTagIds', tagLookup)}
              </div>
              {renderInclusionModeToggle('tagInclusionMode', 'Logic')}
              <div className="library-filters__search-container">
                <Input
                  type="text"
                  placeholder="Search tags..."
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  aria-label="Search tags"
                  leftIcon={
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  }
                  fullWidth
                />
              </div>
              <div className="library-filters__field library-filters__field--multiselect">
                <span className="library-filters__field-helper" id="library-filters-tags-label">
                   Tap to cycle: <strong className="text-include">Include</strong> → <strong className="text-exclude">Exclude</strong> → Clear
                </span>
                {renderTriStateList('tags', filteredTags, 'library-filters-tags-label')}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Content Safety"
              defaultOpen={false}
              summary={getWarningSummary()}
            >
              <div
                className={`library-filters__multiselect-option ${filters.hideMature ? 'library-filters__multiselect-option--exclude' : 'library-filters__multiselect-option--none'}`}
                onClick={() => onChange('hideMature', !filters.hideMature)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onChange('hideMature', !filters.hideMature);
                  }
                }}
                tabIndex={0}
                role="checkbox"
                aria-checked={filters.hideMature ? 'true' : 'false'}
              >
                <div className="library-filters__checkbox-visual">
                  {filters.hideMature && (
                    <svg className="library-filters__state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span className="library-filters__option-label">Hide mature (18+) novels</span>
              </div>

              {contentWarnings.length > 0 && (
                <div className="library-filters__field library-filters__field--multiselect">
                  <span className="library-filters__field-helper" id="library-filters-warnings-label">
                    Exclude novels containing:
                  </span>
                  {renderSelectedChips('excludeContentWarningIds', warningLookup)}
                  <div
                    className="library-filters__multiselect-list"
                    role="group"
                    aria-labelledby="library-filters-warnings-label"
                  >
                    {contentWarnings.map((warning) => {
                      const value = String(warning.id);
                      const excluded = warningSelection.has(value);
                      return (
                        <div
                          key={`warning-${value}`}
                          className={`library-filters__multiselect-option library-filters__multiselect-option--${excluded ? 'exclude' : 'none'}`}
                          onClick={handleWarningClick(value)}
                          onKeyDown={handleWarningKeyDown(value)}
                          tabIndex={0}
                          role="checkbox"
                          aria-checked={excluded ? 'true' : 'false'}
                          title={warning.description || warning.name}
                        >
                          <div className="library-filters__checkbox-visual">
                            {excluded && (
                              <svg className="library-filters__state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            )}
                          </div>
                          <span className="library-filters__option-label">{warning.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CollapsibleSection>
          </div>
        </div>

        <footer className="library-filters__footer">
          <button type="button" className="btn btn--ghost text-secondary" onClick={onReset}>
            Reset All
          </button>
          <div className="library-filters__footer-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary" onClick={onApply}>
              Apply Filters
            </button>
          </div>
        </footer>
      </aside>
    </div>,
    document.body
  );
};

LibraryFiltersPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  filters: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  genres: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })).isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })).isRequired,
  contentWarnings: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })),
};

export default LibraryFiltersPanel;
